import { Asset } from '@clearcover/atlas';
import {
  PaymentMethodType,
  PaymentsAndBillingInfoType,
} from '@clearcover/core-app-types';
import React from 'react';

import { paymentIcon } from 'Shared/constants/paymentIcon';
import {
  formatDateTimeShortUsingTimezone,
  formatMoney,
  formatNumberToOrdinal,
  snakeToTitleCase,
  toTitleCase,
} from 'Shared/utils';

import styles from './styles.module.css';

export const getPaymentMethodIconType = (paymentMethod: PaymentMethodType) => {
  if (paymentMethod.paymentProcessor === 'SWEEP') return 'sweep';
  if (paymentMethod.type === 'creditCard') return paymentMethod.cardType;
  return paymentMethod.type;
};

const paymentFrequencyLabel = (
  numberOfInstallments: number,
  paymentFrequency: PaymentsAndBillingInfoType['paymentFrequency']
) => {
  if (numberOfInstallments === 3) {
    return 'Three Pay';
  } else if (numberOfInstallments === 4) {
    return 'Four Pay';
  } else if (numberOfInstallments === 5) {
    return 'Five Pay';
  } else if (numberOfInstallments === 11 || numberOfInstallments === 12) {
    return 'Twice a month';
  }
  return paymentFrequency === 'Full-Pay' ? 'Full pay' : paymentFrequency;
};

const installmentIntervalLabel = (
  installmentInterval: PaymentsAndBillingInfoType['installmentInterval']
) => {
  switch (installmentInterval) {
    case 'SEMI_MONTHLY':
      return 'Twice a month';
    case 'MONTHLY':
      return 'Monthly';
    case 'WEEKLY':
      return 'Weekly';
    case 'BI_WEEKLY':
      return 'Every other week';
    case 'FULL_PAY':
      return 'Full pay';
    default:
      // Fallback in case new values are added to the enum
      // This will provide a default label for any unknown value so that we do not get runtime errors
      // when new enum values are returned from the backend, but will produce a compile-time error
      // as soon as the enum is updated in the imported models.
      const label: never = installmentInterval;
      return snakeToTitleCase(label);
  }
};

const paymentsDueOnLabel = (
  paymentsDueOn: PaymentsAndBillingInfoType['paymentsDueOn']
) => {
  const { daysOfWeek, daysOfMonth } = paymentsDueOn;

  if (daysOfWeek.length > 0) {
    return toTitleCase(daysOfWeek.join(' & '));
  } else {
    return daysOfMonth.map((day) => formatNumberToOrdinal(day)).join(' & ');
  }
};

export const createBillingSummaryDataPairs = (
  billingInfo?: PaymentsAndBillingInfoType,
  timeZone?: string
) => {
  const {
    hasAutoPay,
    defaultPaymentMethod,
    paidToDate,
    currentBalance,
    overdue,
    totalDue,
    totalTermAmount,
    totalTermCost,
    installmentDue,
    paymentDueDate,
    paymentFrequency,
    paymentsDueOn,
    installmentInterval,
    installments,
    version,
  } = billingInfo || {
    hasAutoPay: false,
    defaultPaymentMethod: {
      cardType: 'visa',
      type: 'creditCard',
      lastFour: '1111',
    },
    paymentFrequency: 'Monthly',
  };
  const isSweep = defaultPaymentMethod.paymentProcessor === 'SWEEP';
  const paymentMethodIconType = getPaymentMethodIconType(
    defaultPaymentMethod as PaymentMethodType
  );

  const numberOfInstallments = installments?.length ?? 0;

  const paymentsDueLabel =
    version === 'v3' ? paymentsDueOnLabel(paymentsDueOn!!) : '';

  return {
    paymentMethodSection: {
      headers: [
        'Scheduled Payment',
        'Payment Due Date',
        'Amount Overdue',
        'Total Payment Due',
        'Payment Method',
        'Auto payment',
        'Payment Frequency',
        ...(paymentsDueLabel ? ['Payments Due On'] : []),
      ],

      data: [
        formatMoney(installmentDue ?? 0),

        paymentDueDate
          ? formatDateTimeShortUsingTimezone(paymentDueDate, timeZone)
          : '',

        formatMoney(overdue ?? 0),

        formatMoney(totalDue ?? 0),

        {
          value: isSweep ? 'In person' : defaultPaymentMethod.lastFour,
          display: (
            <>
              {paymentMethodIconType && (
                <Asset
                  className={styles.creditCardSvg}
                  icon={paymentIcon[paymentMethodIconType]}
                  description={isSweep ? 'in person payment' : 'credit card'}
                  height={12}
                  width={12}
                />
              )}
              {isSweep
                ? 'In person'
                : defaultPaymentMethod.lastFour
                  ? `•••• ${defaultPaymentMethod.lastFour}`
                  : 'None'}
            </>
          ),
        },

        hasAutoPay ? 'Enrolled' : 'Not Enrolled',

        version === 'v3' && installmentInterval
          ? installmentIntervalLabel(installmentInterval)
          : paymentFrequencyLabel(numberOfInstallments, paymentFrequency),

        ...(paymentsDueLabel ? [paymentsDueLabel] : []),
      ],
    },
    paymentInfoSection: {
      headers: [
        'Total Premium',
        'Total Term Cost',
        'Paid to date',
        'Total Balance',
      ],
      data: [
        formatMoney(totalTermAmount ?? 0),

        formatMoney(totalTermCost ?? 0),

        formatMoney(paidToDate ?? 0),

        formatMoney(currentBalance ?? 0),
      ],
    },
  };
};
